import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IUserPreferences } from "../../../../@types/Preferences";
import { IUpdateUserUnitPreferences } from "../../../../@types/User";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { SkeletonLoading } from "../../../../components/Skeleton";
import { AUTH_UNIT_PREFERENCES } from "../../../../constants/localStorageKeys";
import { useUserPreferencesData } from "../../../../hooks/queries/patients";
import { useToast } from "../../../../hooks/useToast";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { userService } from "../../../../services/userService";
import { safeStorage, safeJsonParse } from "../../../../utils/storage";
import {
  PreferencesContainer,
  SectionContainer,
  UnitContainer,
  UnitTitle,
} from "./PreferencesSection.styles";

export function PreferencesSection() {
  const { t } = useTranslation();

  const [temperatureUnit, setTemperatureUnit] = useState("°C");
  const [glucoseUnit, setGlucoseUnit] = useState("mmol/L");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [pefUnit, setPefUnit] = useState("L/sec");
  const [creatinineUnit, setCreatinineUnit] = useState("μmol/L");
  const [heightUnit, setHeightUnit] = useState("cm");

  const [updateUnits, setUpdateUnits] = useState(false);

  const { userPreferences, refetch } = useUserPreferencesData();
  const { showToast } = useToast();

  useEffect(() => {
    if (userPreferences) {
      if (userPreferences.temperatureUnit) {
        setTemperatureUnit(userPreferences.temperatureUnit);
      }
      if (userPreferences.bloodGlucoseUnit) {
        setGlucoseUnit(userPreferences.bloodGlucoseUnit);
      }
      if (userPreferences.weightUnit) {
        setWeightUnit(userPreferences.weightUnit);
      }
      if (userPreferences.flowRateUnit) {
        setPefUnit(userPreferences.flowRateUnit);
      }
      if (userPreferences.creatinineUnit) {
        setCreatinineUnit(userPreferences.creatinineUnit);
      }
      if (userPreferences.heightUnit) {
        setHeightUnit(userPreferences.heightUnit);
      }
    }
  }, [userPreferences]);

  useEffect(() => {
    if (
      userPreferences &&
      updateUnits &&
      (userPreferences.temperatureUnit !== temperatureUnit ||
        userPreferences.bloodGlucoseUnit !== glucoseUnit ||
        userPreferences.weightUnit !== weightUnit ||
        userPreferences.flowRateUnit !== pefUnit ||
        userPreferences.creatinineUnit !== creatinineUnit ||
        userPreferences.heightUnit !== heightUnit)
    ) {
      const unitPreferences: IUpdateUserUnitPreferences = {
        temperatureUnit,
        bloodGlucoseUnit: glucoseUnit,
        weightUnit,
        flowRateUnit: pefUnit,
        creatinineUnit,
        heightUnit,
      };

      userService.updateUserUnitPreferences(unitPreferences).then((result) => {
        const savedPreferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);

        if (savedPreferences) {
          const parsedSavedPreferences = safeJsonParse<IUserPreferences | null>(
            savedPreferences,
            null
          );

          if (parsedSavedPreferences) {
            parsedSavedPreferences.temperatureUnit = temperatureUnit;
            parsedSavedPreferences.bloodGlucoseUnit = glucoseUnit;
            parsedSavedPreferences.weightUnit = weightUnit;
            parsedSavedPreferences.flowRateUnit = pefUnit;
            parsedSavedPreferences.creatinineUnit = creatinineUnit;
            parsedSavedPreferences.heightUnit = heightUnit;

            const newPreferences = JSON.stringify(parsedSavedPreferences);

            safeStorage.setItem(AUTH_UNIT_PREFERENCES, newPreferences);
          }
        }
        refetch();
        setUpdateUnits(false);

        if (result.status >= 200 && result.status < 300) {
          showToast(t("settings_preferences_saved"), "success");
        } else {
          showToast(t("common_genericError"), "error");
        }
      });
    }
  }, [updateUnits]);

  if (!userPreferences) {
    return (
      <SectionContainer>
        <PageTitle>{t("settings_preferences_title")}</PageTitle>

        <PreferencesContainer>
          <UnitContainer>
            <SkeletonLoading width="190px" height="34px" />
            <SkeletonLoading width="130px" height="34px" />
          </UnitContainer>
          <UnitContainer>
            <SkeletonLoading width="190px" height="34px" />
            <SkeletonLoading width="130px" height="34px" />
          </UnitContainer>
          <UnitContainer>
            <SkeletonLoading width="190px" height="34px" />
            <SkeletonLoading width="130px" height="34px" />
          </UnitContainer>
          <UnitContainer>
            <SkeletonLoading width="190px" height="34px" />
            <SkeletonLoading width="130px" height="34px" />
          </UnitContainer>
          <UnitContainer>
            <SkeletonLoading width="190px" height="34px" />
            <SkeletonLoading width="130px" height="34px" />
          </UnitContainer>
          <UnitContainer>
            <SkeletonLoading width="190px" height="34px" />
            <SkeletonLoading width="130px" height="34px" />
          </UnitContainer>
        </PreferencesContainer>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <PageTitle>{t("settings_preferences_title")}</PageTitle>

      <PreferencesContainer>
        <UnitContainer>
          <UnitTitle>{t("settings_preferences_temperature")}</UnitTitle>
          <Dropdown
            value={temperatureUnit}
            trigger={temperatureUnit}
            onValueChange={(e) => {
              setTemperatureUnit(e);
              setUpdateUnits(true);
            }}
          >
            <DropdownTextItem
              text={t("settings_preferences_celsius")}
              value="°C"
            />
            <DropdownTextItem
              text={t("settings_preferences_fahrenheit")}
              value="°F"
            />
          </Dropdown>
        </UnitContainer>
        <UnitContainer>
          <UnitTitle>{t("settings_preferences_glucose")}</UnitTitle>
          <Dropdown
            value={glucoseUnit}
            trigger={glucoseUnit}
            onValueChange={(e) => {
              setGlucoseUnit(e);
              setUpdateUnits(true);
            }}
          >
            <DropdownTextItem text="mmol/L" value="mmol/L" />
            <DropdownTextItem text="mg/dL" value="mg/dL" />
          </Dropdown>
        </UnitContainer>
        <UnitContainer>
          <UnitTitle>{t("settings_preferences_weight")}</UnitTitle>
          <Dropdown
            value={weightUnit}
            trigger={weightUnit}
            onValueChange={(e) => {
              setWeightUnit(e);
              setUpdateUnits(true);
            }}
          >
            <DropdownTextItem
              text={t("settings_preferences_kilograms")}
              value="kg"
            />
            <DropdownTextItem
              text={t("settings_preferences_pounds")}
              value="lb"
            />
            <DropdownTextItem
              text={t("settings_preferences_stones")}
              value="st"
            />
          </Dropdown>
        </UnitContainer>
        <UnitContainer>
          <UnitTitle>{t("settings_preferences_pef")}</UnitTitle>
          <Dropdown
            value={pefUnit}
            trigger={pefUnit}
            onValueChange={(e) => {
              setPefUnit(e);
              setUpdateUnits(true);
            }}
          >
            <DropdownTextItem text="L/sec" value="L/sec" />
            <DropdownTextItem text="L/min" value="L/min" />
          </Dropdown>
        </UnitContainer>
        <UnitContainer>
          <UnitTitle>{t("settings_preferences_creatinine")}</UnitTitle>
          <Dropdown
            value={creatinineUnit}
            trigger={creatinineUnit}
            onValueChange={(e) => {
              setCreatinineUnit(e);
              setUpdateUnits(true);
            }}
          >
            <DropdownTextItem text="μmol/L" value="μmol/L" />
            <DropdownTextItem text="mg/dL" value="mg/dL" />
          </Dropdown>
        </UnitContainer>
        <UnitContainer>
          <UnitTitle>{t("settings_preferences_height")}</UnitTitle>
          <Dropdown
            value={heightUnit}
            trigger={heightUnit}
            onValueChange={(e) => {
              setHeightUnit(e);
              setUpdateUnits(true);
            }}
          >
            <DropdownTextItem
              text={t("settings_preferences_feet")}
              value="ft"
            />
            <DropdownTextItem
              text={t("settings_preferences_centimeters")}
              value="cm"
            />
          </Dropdown>
        </UnitContainer>
      </PreferencesContainer>
    </SectionContainer>
  );
}
